import { useMemo, useRef, useState, useEffect } from "react";
import "./UwRiskMap.scss";
import { motion } from "framer-motion";
import { ScaleLoader } from "react-spinners";
import {
    GoogleMap,
    MarkerF,
    MarkerClustererF,
    CircleF,
    Polygon,
    useLoadScript,
    Autocomplete
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const LIBRARIES = ["places", "geometry"];

const DEFAULT_CENTER = { lat: -1.256763049998577, lng: 36.80433239098705 };


const TEST_RISKS = [
    { id: "r1", name: "Westwood Hotel", lat: 1.2533627987037652, lng: -1.256763049998577, severity: 9, policies: 320, exposure: 185_000_000 },
    { id: "r2", name: "Nova Pioneer Head Office", lat: 36.79678999600102, lng: -1.2568488585279503, severity: 6, policies: 210, exposure: 92_000_000 },
    { id: "r3", name: "Brookside Preparatory School", lat: -1.256904093851657, lng: -1.256904093851657, severity: 4, policies: 150, exposure: 48_000_000 },
    { id: "r4", name: "Fogo Gaucho Westlands", lat: -1.263986057125751, lng: 36.799149838197074, severity: 7, policies: 410, exposure: 210_000_000 },
    { id: "r5", name: "Oshwal College", lat: -1.2597315766416683, lng: 36.81216742576154, severity: 8, policies: 290, exposure: 165_000_000 },
    { id: "r6", name: "Pax Manor Muthaiga", lat: -1.2515071284131736, lng: 36.82363930249091, severity: 5, policies: 120, exposure: 55_000_000 },
    { id: "r7", name: "Kenya National Archives", lat: -1.284175255946872, lng: 36.82896505807619, severity: 8, policies: 90, exposure: 70_000_000 },
    { id: "r8", name: "Hemingways Nairobi", lat: -1.34239314775367, lng: 36.706468823336735, severity: 6, policies: 140, exposure: 60_000_000 },
    { id: "r9", name: "The Hub Karen", lat: -1.3201941061948927, lng: 36.704903637898866, severity: 6, policies: 140, exposure: 60_000_000 },
    { id: "r10", name: "Dojo Wellness Club", lat: -1.3221033345826445, lng: 36.70474270026417, severity: 6, policies: 140, exposure: 60_000_000 },
    { id: "r11", name: "Sky Dale Miotoni 300", lat: -1.3113653916176264, lng: 36.709448689672755, severity: 6, policies: 140, exposure: 60_000_000 },
    { id: "r12", name: "Karen Oasis Restaurant", lat: -1.3286849651131702, lng: 36.71863300595051, severity: 6, policies: 140, exposure: 60_000_000 },
];

function normalizeLatLng(r) {
    let { lat, lng } = r;

    // If swapped (lat looks like 36 and lng like -1), swap them.
    if (Math.abs(lat) > 20 && Math.abs(lng) < 20) {
        [lat, lng] = [lng, lat];
    }

    // If lat is positive near Nairobi (should be negative), flip it.
    if (lat > 0 && Math.abs(lat) < 5 && Math.abs(lng) > 20) {
        lat = -lat;
    }

    return { ...r, lat, lng };
}


function haversineMeters(a, b) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
}

function formatCompactMoney(n) {
    if (n >= 1e12) return `KES ${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9) return `KES ${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `KES ${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `KES ${(n / 1e3).toFixed(1)}K`;
    return `KES ${n.toFixed(0)}`;
}

function severityColor(sev) {
    if (sev >= 8) return "#ef4444";
    if (sev >= 4) return "#fb923c";
    return "#facc15";
}

/** ---------- Subcomponents (same file; drop-in) ---------- */

const RiskToolbar = ({ isLoaded, radius, setRadius, setCenter }) => {
    const [autoComplete, setAutoComplete] = useState(null);
    const navigate = useNavigate();

    const onPlaceChanged = () => {
        if (!autoComplete) return;
        const place = autoComplete.getPlace();
        const loc = place?.geometry?.location;
        if (!loc) return;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
    };

    return (
        <div className="risk-toolbar">
            <div className="toolbar-left">
                {isLoaded ? (
                    <Autocomplete onLoad={(ac) => setAutoComplete(ac)} onPlaceChanged={onPlaceChanged}>
                        <input className="places-input" type="text" placeholder="Search place..." />
                    </Autocomplete>
                ) : (
                    <input className="places-input" type="text" placeholder="Loading places..." disabled />
                )}
            </div>

            <div className="toolbar-right">
                <div className="radius-block">
                    <div className="radius-label">ANALYSIS RADIUS</div>
                    <div className="radius-row">
                        <input
                            type="range"
                            min={250}
                            max={15000}
                            step={250}
                            value={radius}
                            onChange={(e) => setRadius(Number(e.target.value))}
                        />
                        <div className="radius-value">{radius.toLocaleString()}m</div>
                    </div>
                </div>

                <button className="refresh-btn" type="button">
                    Refresh Map
                </button>

                <button className="close-btn" type="button" aria-label="Close" onClick={() => navigate(-1)}>
                    <MdClose size={20} />
                </button>
            </div>
        </div>
    );
};


const RiskMapCanvas = ({
    isLoaded,
    center,
    radius,
    risks,
    totals,
    mapRef,
    selectedRiskId,
    setSelectedRiskId,
    mapStyles
}) => {
    const [pulseScale, setPulseScale] = useState(1);

    // 1) Hooks ALWAYS run
    useEffect(() => {
        const t = setInterval(() => {
            setPulseScale((s) => (s >= 1.35 ? 1 : +(s + 0.03).toFixed(2)));
        }, 40);
        return () => clearInterval(t);
    }, []);

    const circlePath = useMemo(() => {
        // safe guard: when not loaded, return null (but hook still runs)
        if (!isLoaded) return null;
        if (!window.google?.maps?.geometry?.spherical) return null;

        const points = [];
        const steps = 90;

        const centerLL = new window.google.maps.LatLng(center.lat, center.lng);

        for (let i = 0; i <= steps; i++) {
            const heading = (i * 360) / steps;
            const p = window.google.maps.geometry.spherical.computeOffset(centerLL, radius, heading);
            points.push({ lat: p.lat(), lng: p.lng() });
        }

        return points;
    }, [isLoaded, center.lat, center.lng, radius]);

    const maskPaths = useMemo(() => {
        if (!circlePath) return null;

        const world = [
            { lat: 85, lng: -180 },
            { lat: 85, lng: 180 },
            { lat: -85, lng: 180 },
            { lat: -85, lng: -180 },
        ];

        return [world, circlePath];
    }, [circlePath]);

    const selectedRisk = useMemo(() => {
        return risks.find((r) => r.id === selectedRiskId) || null;
    }, [risks, selectedRiskId]);

    // 2) NOW it’s safe to early-return
    if (!isLoaded) return <div className="map-loading">Loading map…</div>;

    return (
        <div className="map-shell">
            <GoogleMap
                onLoad={(map) => (mapRef.current = map)}
                center={center}
                zoom={13}
                mapContainerClassName="map-container"
                options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    clickableIcons: false,
                    styles: mapStyles,
                }}
            >
                {/* Mask outside radius */}
                {maskPaths && (
                    <Polygon
                        paths={maskPaths}
                        options={{
                            fillColor: "#0b1220",
                            fillOpacity: 0.3,   // darker outside
                            strokeOpacity: 0,
                            clickable: false,
                            zIndex: 1,
                        }}
                    />
                )}

                {/* Radius circle outline */}
                <CircleF
                    center={center}
                    radius={radius}
                    options={{
                        fillColor: "#ffffff",
                        fillOpacity: 0.06, // increase a bit for visible “spotlight”
                        strokeOpacity: 0,  // keep outline separate below
                        zIndex: 2,
                        clickable: false,
                    }}
                />


                {/* Risks */}
                <MarkerClustererF
                    options={{
                        minimumClusterSize: 2,
                        gridSize: 50,
                        // keeps clusters from zooming too aggressively
                        maxZoom: 18,
                    }}
                >
                    {(clusterer) =>
                        risks.map((r) => {
                            const isSelected = r.id === selectedRiskId;

                            return (
                                <MarkerF
                                    key={r.id}
                                    position={{ lat: r.lat, lng: r.lng }}
                                    title={r.name}
                                    zIndex={isSelected ? 999 : 10}
                                    onClick={() => setSelectedRiskId(r.id)}
                                />
                            );
                        })
                    }
                </MarkerClustererF>


                {/* Standout pulse for selected risk */}
                {selectedRisk && (
                    <CircleF
                        center={{ lat: selectedRisk.lat, lng: selectedRisk.lng }}
                        radius={220 * pulseScale}
                        options={{
                            fillColor: "#ef4444",
                            fillOpacity: 0.06,
                            strokeColor: "#ef4444",
                            strokeOpacity: 0.45,
                            strokeWeight: 2,
                            zIndex: 998,
                            clickable: false,
                        }}
                    />
                )}


                {/* KPI overlay */}
                <div className="kpi-card">
                    <div className="kpi-item">
                        <div className="kpi-label">ACTIVE POLICIES</div>
                        <div className="kpi-value">{totals.activePolicies.toLocaleString()}</div>
                    </div>
                    <div className="kpi-divider" />
                    <div className="kpi-item">
                        <div className="kpi-label">EXPOSURE VALUE</div>
                        <div className="kpi-value">{formatCompactMoney(totals.exposureValue)}</div>
                    </div>
                </div>

                {/* Legend overlay */}
                <div className="legend-card">
                    <div className="legend-title">RISK SEVERITY SCALE</div>
                    <div className="legend-row">
                        <span className="dot" style={{ background: "#ef4444" }} />
                        <span>High Risk</span>
                        <span className="muted">(8-10)</span>
                    </div>
                    <div className="legend-row">
                        <span className="dot" style={{ background: "#fb923c" }} />
                        <span>Medium Risk</span>
                        <span className="muted">(4-7)</span>
                    </div>
                    <div className="legend-row">
                        <span className="dot" style={{ background: "#facc15" }} />
                        <span>Low Risk</span>
                        <span className="muted">(0-3)</span>
                    </div>
                </div>
            </GoogleMap>
        </div>
    );
};


const RiskList = ({ risks }) => {
    return (
        <div className="risk-list">
            {risks.map((r) => (
                <div key={r.id} className="risk-row">
                    <div className="risk-dot" style={{ background: severityColor(r.severity) }} />
                    <span className="risk-name">{r.name}</span>
                    <span className="risk-meta">sev {r.severity}</span>
                </div>
            ))}
        </div>
    );
};

/** ---------- Main ---------- */

const UwRiskMap = () => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const [isProcessing, setIsProcessing] = useState(false);
    const [center, setCenter] = useState(DEFAULT_CENTER);
    const [radius, setRadius] = useState(2500);

    const [selectedRiskId, setSelectedRiskId] = useState(null);


    const mapRef = useRef(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries: LIBRARIES,
        version: "weekly",
    });

    const normalizedRisks = useMemo(() => TEST_RISKS.map(normalizeLatLng), []);

    const filteredRisks = useMemo(() => {
        return normalizedRisks.filter((r) => haversineMeters(center, { lat: r.lat, lng: r.lng }) <= radius);
    }, [normalizedRisks, center, radius]);

    const totals = useMemo(() => {
        const activePolicies = filteredRisks.reduce((sum, r) => sum + (r.policies || 0), 0);
        const exposureValue = filteredRisks.reduce((sum, r) => sum + (r.exposure || 0), 0);
        return { activePolicies, exposureValue };
    }, [filteredRisks]);

    useEffect(() => {
        if (!isLoaded || !mapRef.current || !window.google?.maps?.geometry?.spherical) return;

        const map = mapRef.current;

        // Compute bounds of the radius circle using 4 cardinal points
        const centerLL = new window.google.maps.LatLng(center.lat, center.lng);

        const north = window.google.maps.geometry.spherical.computeOffset(centerLL, radius, 0);
        const east = window.google.maps.geometry.spherical.computeOffset(centerLL, radius, 90);
        const south = window.google.maps.geometry.spherical.computeOffset(centerLL, radius, 180);
        const west = window.google.maps.geometry.spherical.computeOffset(centerLL, radius, 270);

        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(north);
        bounds.extend(east);
        bounds.extend(south);
        bounds.extend(west);

        map.fitBounds(bounds, 40); // padding in px
    }, [isLoaded, center.lat, center.lng, radius, filteredRisks]);


    if (loadError) return <div className="uw-risk-map-error">Google Maps failed to load.</div>;

    const MAP_STYLES = [
        {
            "featureType": "all",
            "elementType": "labels",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "all",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "saturation": 36
                },
                {
                    "color": "#000000"
                },
                {
                    "lightness": 40
                }
            ]
        },
        {
            "featureType": "all",
            "elementType": "labels.text.stroke",
            "stylers": [
                {
                    "visibility": "on"
                },
                {
                    "color": "#000000"
                },
                {
                    "lightness": 16
                }
            ]
        },
        {
            "featureType": "all",
            "elementType": "labels.icon",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "administrative",
            "elementType": "geometry.fill",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 20
                }
            ]
        },
        {
            "featureType": "administrative",
            "elementType": "geometry.stroke",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 17
                },
                {
                    "weight": 1.2
                }
            ]
        },
        {
            "featureType": "administrative.country",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#e5c163"
                }
            ]
        },
        {
            "featureType": "administrative.locality",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#c4c4c4"
                }
            ]
        },
        {
            "featureType": "administrative.neighborhood",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#e5c163"
                }
            ]
        },
        {
            "featureType": "landscape",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 20
                }
            ]
        },
        {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 21
                },
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "poi.business",
            "elementType": "geometry",
            "stylers": [
                {
                    "visibility": "on"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "geometry.fill",
            "stylers": [
                {
                    "color": "#e5c163"
                },
                {
                    "lightness": "0"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "geometry.stroke",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#ffffff"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "labels.text.stroke",
            "stylers": [
                {
                    "color": "#e5c163"
                }
            ]
        },
        {
            "featureType": "road.arterial",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 18
                }
            ]
        },
        {
            "featureType": "road.arterial",
            "elementType": "geometry.fill",
            "stylers": [
                {
                    "color": "#575757"
                }
            ]
        },
        {
            "featureType": "road.arterial",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#ffffff"
                }
            ]
        },
        {
            "featureType": "road.arterial",
            "elementType": "labels.text.stroke",
            "stylers": [
                {
                    "color": "#2c2c2c"
                }
            ]
        },
        {
            "featureType": "road.local",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 16
                }
            ]
        },
        {
            "featureType": "road.local",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#999999"
                }
            ]
        },
        {
            "featureType": "transit",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 19
                }
            ]
        },
        {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#000000"
                },
                {
                    "lightness": 17
                }
            ]
        }
    ];


    return (
        <motion.div className="uw-risk-map" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {isProcessing ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <div className="uw-risk-map-layout">
                    <RiskToolbar
                        isLoaded={isLoaded}
                        radius={radius}
                        setRadius={setRadius}
                        setCenter={setCenter}
                    />

                    <RiskMapCanvas
                        isLoaded={isLoaded}
                        center={center}
                        radius={radius}
                        risks={filteredRisks}
                        totals={totals}
                        mapRef={mapRef}
                        selectedRiskId={selectedRiskId}
                        setSelectedRiskId={setSelectedRiskId}
                        mapStyles={MAP_STYLES}
                    />

                    <RiskList risks={filteredRisks} />
                </div>
            )}
        </motion.div>
    );
};

export default UwRiskMap;
